import {
  isEditorContentEmpty,
  removeHTMLTags,
} from "@utils/helper/index.helper";
import { fi } from "date-fns/locale";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { z } from "zod";
const slugSchema = z
  .string({ required_error: "Slug is required" })
  .regex(/^[a-z]+(?:-[a-z]+)*$/, {
    message:
      "Slug must be a valid format (lowercase letters only, hyphen-separated, no consecutive hyphens)",
  })
  .refine((value) => !value.includes("--"), {
    message: "Slug cannot contain consecutive hyphens",
  })
  .optional()
  .nullable();
const imageSchema = z
  .union([
    z.instanceof(File, { message: "Must be a valid file" }),
    z.instanceof(Blob, { message: "Must be a valid file" }),
    z.string({ required_error: "Image is requried" }),
  ])
  .refine((value) => value !== null && value !== undefined, {
    message: "File must be uploaded",
  });
const phoneSchema = z
  .string({ required_error: "Phone Number is Required" })
  .refine(
    (value) => {
      const cleaned = value.startsWith("+") ? value : "+" + value;
      try {
        const phoneNumber = parsePhoneNumberFromString(cleaned);
        return phoneNumber?.isValid() ?? false;
      } catch {
        return false;
      }
    },
    {
      message: "Enter a valid phone number",
    },
  );
const passwordValidation = z
  .string()
  .min(5, { message: "Password must be at least 5 characters long." })
  .regex(/[A-Z]/, {
    message: "Password must contain at least one uppercase letter.",
  })
  .regex(/[a-z]/, {
    message: "Password must contain at least one lowercase letter.",
  })
  .regex(/\d/, { message: "Password must contain at least one number." })
  .regex(/[!@#$%^&*()_+\-={};':"\\|,.<>/?]/g, {
    message:
      "Password must contain at least one special character (@, $, !, %, *, ?, &).",
  });

const urlSchema = z
  .string()
  .regex(
    /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{1,}(:\d+)?(\/[^\s]*)?(\?[^\s]*)?(#[^\s]*)?\/?$/,
    {
      message:
        "Invalid URL format (must be a valid domain with or without 'www', and optional valid protocol)",
    },
  );
const numericStringWithDecimal = z.string().regex(/^\d+(\.\d+)?$/, {
  message: "Must be a valid number (positive, optional decimal)",
});
const wholeNumber = z.string().regex(/^\d+$/, {
  message: "Must be a whole number",
});

const AsyncSchema = z.array(
  z.object(
    {
      label: z.union([z.string(), z.number()], { message: "required" }),
      value: z.union([z.string(), z.number()], { message: "required" }),
    },
    { required_error: "please select option" },
  ),
);

const NewPasswordSchema = z.object({
  new_password: passwordValidation,
  confirm_new_password: z.string(),
});

const emailSchema = z
  .string({ message: "valid email is required" })
  .email()
  .transform((val) => val.toLowerCase());

NewPasswordSchema.refine(
  (data) => data.new_password === data.confirm_new_password,
  {
    message: "Passwords do not match",
  },
);

const UserLoginSchema = z.object({
  password: z.string().min(3, { message: "Password is required" }),
  email: z.string().email(),
  isRemember: z.boolean().optional(),
});

const UserForgetSchema = z.object({
  email: z.string().email(),
});
const UserVerifyOtpSchema = z.object({
  email: z.string().email().optional(),
  otp: z.string(),
});

const SelectSchema = z.object({
  label: z.string({ required_error: "Course Batch is required" }),
  value: z.number({ required_error: "Course Batch is required" }),
});

const UserResetPasswordSchema = z
  .object({
    password: passwordValidation,
    cpassword: z.string(),
  })
  .refine((data) => data.password === data.cpassword, {
    path: ["cpassword"],
    message: "Passwords do not match.",
  });

const JourneyFormSchema = z.object({
  title: z
    .string({ message: "Title is required" })
    .min(5, { message: "title must be above 5 character" }),
  section: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "section is required",
  }),
  slug: slugSchema,
  image: imageSchema,
  header_code: z.string({ message: "Header Code is required" }).optional(),
  footer_code: z.string({ message: "Footer Code is required" }).optional(),
});

const BrandingFormSchema = z.object({
  title: z
    .string({ message: "Title is required" })
    .min(5, { message: "title must be above 5 character" }),
  branding: z
    .array(
      z.object(
        {
          label: z.union([z.string(), z.number()], { message: "required" }),
          value: z.union([z.string(), z.number()], { message: "required" }),
        },
        { required_error: "please select option" },
      ),
    )
    .min(1, { message: "Branding is required" }),
  certificate_template: z
    .array(
      z.object(
        {
          label: z.union([z.string(), z.number()], { message: "required" }),
          value: z.union([z.string(), z.number()], { message: "required" }),
        },
        { required_error: "please select option" },
      ),
    )
    .min(1, { message: "Branding is required" }),
  image: imageSchema,
  header_code: z.string({ message: "Header Code is required" }).optional(),
  footer_code: z.string({ message: "Footer Code is required" }).optional(),
});

const CareerSchema = z.object({
  title: z.string(),
  description: z.string(),
  position: SelectSchema,
  num_of_vacancy: z.number().nullable(),
  apply_link: z.string().url(),
  image: imageSchema,
  is_show: z.boolean(),
  enable_auto_expiration: z.boolean(),
  expiration_date: z.string().nullable(),
  is_expired: z.boolean(),
  experience_level: SelectSchema,
});

const CareerFormSchema = z
  .object({
    title: z
      .string({ message: "Title is required" })
      .min(1, "Job Title is required"),
    experience_level: AsyncSchema.refine(
      (val) => val !== null && val.length > 0,
      { message: "experience level is required" },
    ),
    description: z
      .string()
      .min(1, "Description is required")
      .refine((val) => val.length >= 2, {
        message: "Description is required",
      }),
    image: imageSchema,
    num_of_vacancy: z
      .string()
      .refine((val) => !isNaN(Number(val)), {
        message: "Number of vacancy must be a number",
      })
      .optional(),

    enable_auto_expiration: z.boolean(),
    apply_link: z.union([
      urlSchema,
      z.string({ required_error: "Upload link is required" }),
    ]),
    expiration_date: z.union([z.string(), z.date()]).nullable().optional(),
    header_code: z.string({ message: "Header Code is required" }).optional(),
    footer_code: z.string({ message: "Footer Code is required" }).optional(),
    meta_title: z
      .string({ message: "Enter valid meta title" })
      .optional()
      .nullable(),
    meta_description: z
      .string({ message: "Enter valid meta description" })
      .optional()
      .nullable(),
  })
  .refine(
    (val) => {
      if (!val.expiration_date) return true;
      const date = new Date(val.expiration_date);
      const today = new Date();
      return date > today;
    },
    {
      path: ["expiration_date"],
      message: "Expiration date should be in the future ",
    },
  )
  .superRefine((data, ctx) => {
    if (data.enable_auto_expiration) {
      if (data.expiration_date === undefined || data.expiration_date === null) {
        ctx.addIssue({
          path: ["expiration_date"],
          code: z.ZodIssueCode.custom,
          message:
            "expiration date is required when auto expiration is enabled",
        });
      }
    }
  });

const faqSchema = z.object({
  question: z
    .string({ required_error: "Question is required" })
    .min(1, "Question is required"),
  description: z
    .string({ required_error: "Answer is required" })
    .min(1, "Answer is required")
    .refine((val) => val.length >= 2, {
      message: "About is Required",
    }),
  header_code: z.string({ message: "Header Code is required" }).optional(),
  footer_code: z.string({ message: "Footer Code is required" }).optional(),
  json_ld: z.any().optional(),
});

const testimonialDescripionSchema = z.object({
  name: z
    .string({ required_error: "Name is required" })
    .min(1, "Name is required"),
  slug: slugSchema,
  role: z
    .string({ required_error: "Role is required" })
    .min(1, "Role is required"),
  description: z
    .string({ required_error: "Description is required" })
    .refine((val) => !isEditorContentEmpty(val), {
      message: "Description must contain valid text.",
    })
    .refine(
      (val) => {
        const plainText = val
          .replace(/<[^>]*>/g, "") // remove HTML tags
          .replace(/&nbsp;/gi, " ") // replace non-breaking spaces
          .replace(/\s+/g, " ") // normalize spaces
          .trim();
        return plainText.length <= 315;
      },
      {
        message: "Description must be at most 320 characters of visible text.",
      },
    )
    .refine((val) => !/<img\s[^>]*src=["']?([^>"']+)["']?[^>]*>/i.test(val), {
      message: "Images are not allowed in the description",
    }),
  image: imageSchema,
  header_code: z.string({ message: "Header Code is required" }).optional(),
  footer_code: z.string({ message: "Footer Code is required" }).optional(),
  json_ld: z.any().optional(),
});

const testimonialVideoSchema = z
  .object({
    name: z
      .string({ required_error: "name is required" })
      .min(1, "Name is required"),
    role: z
      .string({ required_error: "role is required" })
      .min(1, "Role is required"),
    video: imageSchema,
    image: imageSchema,
    // video_url: z
    //   .string()
    //   .regex(
    //     /^(?!.*(?:youtube\.com|youtu\.be))(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{1,}(:\d+)?(\/[^\s]*)?(\?[^\s]*)?(#[^\s]*)?\/?$/,
    //     {
    //       message:
    //         "Invalid URL format (must be a valid domain with or without 'www', and optional valid protocol, YouTube URLs are not allowed)",
    //     }
    //   )
    //   .optional(),
    slug: slugSchema,
    header_code: z.string({ message: "Header Code is required" }).optional(),
    footer_code: z.string({ message: "Footer Code is required" }).optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.video) {
      ctx.addIssue({
        path: ["video"],
        code: z.ZodIssueCode.custom,
        message: "Video is required when video URL is not provided",
      });
    }
  });

const TemplateFormSchmea = z.object({
  title: z.string({ message: "Title is required" }),
  course_batch: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "course batch is required",
  }),
  html_content: z.string({ required_error: "HTML Editor is required" }),
  header_code: z.string({ message: "Header Code is required" }).optional(),
  footer_code: z.string({ message: "Footer Code is required" }).optional(),
});

const TemplateFormSchmeaGiveaway = z.object({
  name: z.string({ message: "Name is required" }),
  prefix: z.string({ message: "Prefix is required" }),
  html_content: z.string({ required_error: "HTML Editor is required" }),
  header_code: z.string({ message: "Header Code is required" }).optional(),
  footer_code: z.string({ message: "Footer Code is required" }).optional(),
});

const FormsFormSchema = z
  .object({
    title: z.string({ message: "Title is required" }),
    description: z
      .string({ message: "Description is required" })
      .refine((val) => val.length >= 2, {
        message: "Description is required",
      }),
    header_code: z.string({ message: "Header Code is required" }).optional(),
    embedded_code: z
      .string({ message: "Embedded Code is required" })
      .optional(),
    meta_title: z.string({ message: "Enter valid meta title" }),
    meta_description: z.string({ message: "Enter valid meta description" }),
    media: imageSchema,
    excerpt: z.string({ message: "Excerpt is required" }),
    auto_expiration: z.boolean(),
    auto_expiration_date: z.string().optional(),
    category: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "category is required",
    }),
    slug: slugSchema,
  })
  .refine(
    (val) => {
      if (!val.auto_expiration) return true; // if not auto-expiration, skip
      if (!val.auto_expiration_date) return false; // must exist
      const date = new Date(val.auto_expiration_date);
      const today = new Date();
      return date > today;
    },
    {
      path: ["auto_expiration_date"],
      message:
        "Expiration date is required and must be in the future when auto_expiration is true",
    },
  )
  .superRefine((data, ctx) => {
    if (data.auto_expiration) {
      if (
        data.auto_expiration_date === undefined ||
        data.auto_expiration_date === null
      ) {
        ctx.addIssue({
          path: ["auto_expiration_date"],
          code: z.ZodIssueCode.custom,
          message:
            "expiration date is required when auto expiration is enabled",
        });
      }
    }
  });

const TeamMemberSchema = z
  .object({
    first_name: z.string({ required_error: "First name is required" }),
    last_name: z.string({ required_error: "Last name is required" }),
    role: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "role is required",
    }),
    designation: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "designation is required",
    }),
    email: z.string({ required_error: "Email is required" }),
    image: imageSchema,
    password: passwordValidation.optional(),
    is_masterclass: z.boolean().optional(),
    confirm_password: z.string().optional(),
    is_superuser: z.boolean().optional(),
    bio: z.string().optional().nullable(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Passwords don't match",
    path: ["confirm_password"], // Target this field for the error
  });

const StudentFormSchema = z.object({
  // certificate_template: AsyncSchema.refine(
  //   (val) => val !== null && val.length > 0,
  //   {
  //     message: "certificate template is required",
  //   }
  // ),
  course_batch: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "course & batch is required",
  }),
  first_name: z.string({ required_error: "First Name is required" }),
  last_name: z.string({ required_error: "Last Name is required" }),
  phone: z.union([phoneSchema, z.literal("")]).optional().nullable(),
  email: z
    .union([z.string().email("Invalid email address"), z.literal("")])
    .optional()
    .nullable(),
  current_position: z.any().optional(),
  professional_image: imageSchema.optional().nullable(),
  // current_position:z.string(),
  start_date: z
    .string({ required_error: "Start Date is required" })
    .optional()
    .nullable(),
  end_date: z
    .string({ required_error: "End Date is required" })
    .optional()
    .nullable(),
  description: z
    .string({ required_error: "Description is required" })
    .optional()
    .nullable(),
});

const SuccessStoriesSchema = z.object({
  title: z
    .string({ message: "Title is required" })
    .min(3, "Title Must be Greater Than 3 Charecter"),

  feature_quotation: z
    .string({ message: "Feature Quotation is Required" })
    .refine((val) => typeof val === "string" && val.length >= 3, {
      message: "Feature Quotation is Required",
    })
    .refine((val) => typeof val === "string" && val?.length <= 250, {
      message: "Feature Quotation should have less than 250 characters",
    }),

  featured_image: imageSchema,
  slug: slugSchema,
  testimonial_video: imageSchema.optional().nullable(),
  items: z.any(),
  student: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "student is required",
  }),
  team_member: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "team member is required",
  }),
  header_code: z.string({ message: "Header Code is required" }).optional(),
  footer_code: z.string({ message: "Footer Code is required" }).optional(),
  json_ld: z.any().optional(),
  working_at: z.string({ message: "Working At is required" }).optional(),
  working_position: z
    .string({ message: "Working Position is required" })
    .optional(),
  course_link: z.string({ message: "Course Link is required" }).optional(),
});

const PopupSchema = z.object({
  name: z
    .string({ message: "Popup Title is Required" })
    .min(3, "Popup Title must be at least 3 characters long"),
  link: z
    .string({ message: "Popup Link is Required" })
    .regex(/^(https:\/\/|www\.)[a-zA-Z0-9][\w\-\.]+\.[a-zA-Z]{2,}(\/\S*)?$/, {
      message:
        "Link must start with 'https://' or 'www.' and be a valid URL format",
    }),
  duration_in_seconds: z.union([
    z.string({ message: "Duration is Required" }),
    z.number({ message: "Duration is Required" }),
  ]),
  is_skipable: z.boolean({ message: "is_skippable is required" }).optional(),
  is_show: z.boolean({ message: "is_skippable is required" }).optional(),
  image: imageSchema,
});
const BucketSchema = z.object({
  image: imageSchema,
});
const MomentsSchema = z.object({
  title: z
    .string({ message: "Title is required" })
    .min(3, "Title Must be Greater Than 3 Charecter"),
  thumbnail: imageSchema,
  video: imageSchema,
  header_code: z.string({ message: "Header Code is required" }).optional(),
  footer_code: z.string({ message: "Footer Code is required" }).optional(),
});

const courseSchema = z
  .object({
    title: z
      .string({ message: "Title is required" })
      .min(3, "Please enter a valid title"),
    level: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "level is required",
    }),
    slug: slugSchema,
    registration_fee: z
      .string({ message: "Registration is Required" })
      .min(1, { message: "Registration is required" })
      .optional(),
    discount: z.any({ message: "Discount is required" }).optional(),
    excerpt: z.string({ message: "Excerpt is required" }),
    amount: z.any({ message: "Total Amount is required" }).optional(),
    is_masterclass: z.boolean().optional(),
    is_jrp: z.boolean().optional(),
    course_type: z.any().optional(),
    is_price: z
      .boolean({ message: "Enable Payment is required" })
      .default(false)
      .optional(),
    is_discount: z
      .boolean({ message: "Enable Discount is required" })
      .default(false)
      .optional(),
    header_code: z
      .string({ message: "Enter valid header code" })
      .optional()
      .nullable(),
    footer_code: z
      .string({ message: "Enter valid footer code" })
      .optional()
      .nullable(),
    feature_image: z.string({ message: "Invalid Url" }),
    featured_video: z.string({ message: "Invalid Url" }).optional(),
    cover_image: z.string({ message: "Invalid Url" }),
    meta_title: z
      .string({ message: "Enter valid meta title" })
      .optional()
      .nullable(),
    meta_description: z
      .string({ message: "Enter valid meta description" })
      .optional()
      .nullable(),
    // feature_image: imageSchema,
    // featured_video: imageSchema.optional(),
    // cover_image: imageSchema,
    icons: z.array(z.any()).min(1, { message: "icons is required" }),
    syllabus_link: z.string().optional().nullable(),
    course_link: z.string().optional().nullable(),
    mentors_overview: z.string().optional().nullable(),
    // items: z.array(
    //   z
    //     .object({
    //       type: z.string(),
    //       id: z.union([z.string(), z.number()]),
    //       title: z.string(),
    //       description: z.any().nullable(),
    //       image: imageSchema.optional(),
    //       children: z
    //         .array(
    //           z.object({
    //             id: z.union([z.string(), z.number()]),
    //             question: z.string(),
    //             answer: z.string(),
    //           })
    //         )
    //         .optional(),
    //     })
    //     .optional()
    //     .nullable()
    // ),
    items: z.any(),
    categories: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "category is required",
    }),
    course_state: z.array(z.any()).optional(),
    course_state_type_multi: z.array(z.enum(["1", "2", "3"])).optional(),
    discount_type: z
      .array(
        z.object({
          label: z.string(),
          value: z.string(),
        }),
      )
      .optional(),
    duration: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "duration is required",
    }),
    learning_format: AsyncSchema.refine(
      (val) => val !== null && val.length > 0,
      {
        message: "learning format is required",
      },
    ),
    mentor: AsyncSchema.optional().nullable(),
    currency: z
      .array(
        z.object({
          label: z.string(),
          value: z.string(),
        }),
      )
      .optional(),
    is_enable_payment: z.boolean().nullable().optional(),
    configuration: z.any().optional(),
    is_ai_integrated: z.boolean().default(false).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.is_price) {
      if (!data.currency || data.currency.length === 0) {
        ctx.addIssue({
          path: ["currency"],
          code: z.ZodIssueCode.custom,
          message: "Currency is required when pricing is enabled",
        });
      }

      if (data.amount === undefined || data.amount === null) {
        ctx.addIssue({
          path: ["amount"],
          code: z.ZodIssueCode.custom,
          message: "Amount is required when pricing is enabled",
        });
      }
    }
    if (data.is_price && data.is_discount) {
      if (!data.discount_type || data.discount_type.length === 0) {
        ctx.addIssue({
          path: ["discount_type"],
          code: z.ZodIssueCode.custom,
          message: "discount type is required when discount is enabled",
        });
      }

      if (data.discount === undefined || data.discount === null) {
        ctx.addIssue({
          path: ["discount"],
          code: z.ZodIssueCode.custom,
          message: "discount is required when discount is enabled",
        });
      }
    }
  });

const BatchSchema = z.object({
  name: z
    .string({ required_error: "name is required" })
    .min(3, { message: "name must be above 3 character" })
    .max(255, { message: "name must be below 255 character" }),
  is_masterclass: z.boolean().optional(),
});

const CourseBatchSchema = z
  .object({
    name: z
      .string({ required_error: "name is required" })
      .min(3, { message: "name must be above 3 characters" })
      .max(255, { message: "name must be below 255 characters" }),

    start_date: z
      .string()
      .refine(
        (val) => {
          if (val === null || val === "") return true;
          const date = new Date(val);
          return !isNaN(date.getTime());
        },
        { message: "Start date is not valid" },
      )
      .optional()
      .nullable(),

    end_date: z
      .string()
      .refine(
        (val) => {
          if (val === null || val === "") return true;
          const date = new Date(val);
          return !isNaN(date.getTime());
        },
        { message: "End date is not valid" },
      )
      .optional()
      .nullable(),

    course: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "course format is required",
    }),
    is_masterclass: z.boolean().optional(),
    is_jrp: z.boolean().optional(),
    batch: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "batch format is required",
    }),
  })
  .superRefine((data, ctx) => {
    const { start_date, end_date } = data;

    const hasStart = !!start_date;
    const hasEnd = !!end_date;

    // Rule: If one date is provided, the other is required
    if ((hasStart && !hasEnd) || (!hasStart && hasEnd)) {
      if (!hasStart) {
        ctx.addIssue({
          path: ["start_date"],
          code: z.ZodIssueCode.custom,
          message: "Start date is required when end date is provided",
        });
      }
      if (!hasEnd) {
        ctx.addIssue({
          path: ["end_date"],
          code: z.ZodIssueCode.custom,
          message: "End date is required when start date is provided",
        });
      }
    }

    // Rule: start must be before end
    if (hasStart && hasEnd) {
      const start = new Date(start_date!);
      const end = new Date(end_date!);

      if (start >= end) {
        ctx.addIssue({
          path: ["end_date"],
          code: z.ZodIssueCode.custom,
          message: "End date must be after start date",
        });
      }
    }
  });

const CategorySchema = z.object({
  name: z
    .string({ required_error: "name is required" })
    .min(3, { message: "name must be above 3 character" })
    .max(255, { message: "name must be below 255 character" }),
  is_masterclass: z.boolean().optional(),
});
const DurationSchema = z.object({
  duration: z
    .string({ required_error: "duration is required" })
    .min(3, { message: "duration name must be above 3 character" })
    .max(255, { message: "duration must be below 255 character" }),
  is_masterclass: z.boolean().optional(),
});

const placementSchema = z.object({
  student: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "student is required",
  }),
  placement_company: z.string({
    required_error: "Placement Company is required",
  }),
  role: z.string({ required_error: "Role is required" }),
});
const PaymentSchema = z
  .object({
    date: z
      .union([z.string({ required_error: "Date is required" }), z.date()])
      .refine(
        (data) => {
          const date = typeof data === "string" ? new Date(data) : data;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          date.setHours(0, 0, 0, 0);
          return date <= today;
        },
        {
          message: "Date cannot be in the future",
        },
      ),
    payment_number: z.any().optional().nullable(),
    student: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "student is required",
    }),
    invoice: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "invoice is required",
    }),
    course_batch: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "course & batch is required",
    }),
    payment_mode: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "payment mode is required",
    }),
    discount_type: z.any().optional(),
    discount: AsyncSchema.refine((val) => val !== null, {
      message: "discount is required",
    }).optional(),
    custom_discount: z.string().optional(),
    discount_code: z.string().optional(),
    currency: z
      .array(
        z.object({
          label: z.string(),
          value: z.string(),
        }),
      )
      .optional(),
    total_paid_amount: z
      .string({
        required_error: "Payment Amount is required",
      })
      .min(3, {
        message: " Required",
      }),
    is_masterclass: z.boolean().optional(),
    is_jrp: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.discount_type) return;
    if (data.discount_type === "discount_type") {
      if (data.discount === undefined || data.discount === null) {
        ctx.addIssue({
          path: ["discount"],
          code: z.ZodIssueCode.custom,
          message: "discount is required when auto discount type is selected",
        });
      }
    }
    if (data.discount_type === "discount_code") {
      if (data.discount === undefined || data.discount === null) {
        ctx.addIssue({
          path: ["discount_code"],
          code: z.ZodIssueCode.custom,
          message: "discount is required when auto discount type is selected",
        });
      }
    }
  });
const PaymentModeSchema = z.object({
  name: z
    .string({ required_error: "Mode Name is required" })
    .min(2, { message: "Mode name must be 2 characters" }),
});

const PaymentDiscountSchema = z.object({
  name: z.string({ required_error: "Discount Name is required" }),
  discount_type: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "discount type is required",
  }),
  limit: numericStringWithDecimal,
  value: numericStringWithDecimal,
});

const CustomPageSchema = z.object({
  title: z.string({ message: "Title is required" }),
  url: z
    .string({ required_error: "Url is required" })
    .regex(/^[a-zA-Z0-9\-_]+$/, {
      message:
        "URL can only contain letters, numbers, hyphens, and underscores",
    }),
  html_content: z.string({ required_error: "Content is required" }),
  header_code: z.string({ message: "Header Code is required" }).optional(),
  footer_code: z.string({ message: "Embedded Code is required" }).optional(),
});

const IndustryAlliesSchema = z.object({
  name: z
    .string({ required_error: "Name is required" })
    .min(3, "Name Must be Greater Than 3 Charecter"),
  image: imageSchema,
});
const MentorSchema = z.object({
  first_name: z
    .string({ required_error: "Name is required" })
    .min(3, "First Name Must be Greater Than 3 Charecter"),
  last_name: z
    .string({ required_error: "Last Name is required" })
    .min(3, "Last Name Must be Greater Than 3 Charecter"),
  email: z.string({ required_error: "Email is required" }).email(),
  designation: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "designation is required",
  }),
  about: z
    .string({ required_error: "About is Required" })
    .min(3, "About the mentor is required")
    .refine((val) => val.length >= 2, {
      message: "About is Required",
    }),

  // .refine(
  //   (val) => {
  //     const cleaned = removeHTMLTags(val);
  //     return cleaned.length <= 250;
  //   },
  //   (val) => {
  //     const cleaned = removeHTMLTags(val);
  //     return {
  //       message: `Master Description should be less than 250 characters (currently ${cleaned.length} without formatting)`,
  //     };
  //   }
  // ),
  id: z.string().optional().nullable(),
  image: imageSchema,
  doodle_image: imageSchema,
  quote: z.string().optional().nullable(),
});

const TeamsLicenseSchema = z.object({
  // first_name: z.string({ required_error: "First Name is required" }),
  // last_name: z.string({ required_error: "Last Name is required" }),
  teams_email: z
    .string({ required_error: "Teams Email is required" })
    .email("Invalid email address"),
  student: z.array(
    z.object({
      label: z.string(),
      value: z.number(),
    }),
  ),
  teams_password: passwordValidation,
  is_coding: z.boolean().optional(),
});

const studentSchema = z.object({
  full_name: z.string({ required_error: "Full Name is required" }),
  email: z
    .string({ required_error: "Email is required" })
    .email("Invalid email address"),
  phone_number: phoneSchema,
  course_batch: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "course & batch is required",
  }),
  professional_image: imageSchema.optional().nullable(),
});

// Define the form schema (array of students)
const enrollmentSchema = z.object({
  students: z.array(studentSchema).nonempty("At least one student is required"),
});
const singleStudentEnrollmentSchema = studentSchema;
const ContactUsSchema = z.object({
  full_name: z.string({ required_error: "Full Name is required" }),
  email: z.string({ required_error: "Email is required" }).email(),
  phone_number: phoneSchema,
  address: z.string({ required_error: "Address is required" }),
  subject: z.string({ required_error: "Subject is required" }),
  query: z.string({ required_error: "Message is required" }),
  course: z.any().optional(),
  learning_format: z.any().optional(),
  has_laptop: z.boolean().optional(),
});

const ContactUsMasterClassSchema = z.object({
  full_name: z.string({ required_error: "Full Name is required" }),
  email: z.string({ required_error: "Email is required" }).email(),
  phone_number: phoneSchema,
  course: z.any().optional(),
  address: z.string({ required_error: "Address is required" }),
  profession: z.string().optional(),
  query: z.string({ required_error: "Message is required" }),
  learning_format: z.any().optional(),
  has_laptop: z.boolean().optional(),
});

const certificateSchema = z
  .object({
    student: z.any(),
    course_batch: AsyncSchema.refine((val) => val !== null && val.length > 0, {
      message: "course & batch is required",
    }),
    glimpse: imageSchema.optional(),
    enrollment_date: z
      .union([z.date(), z.string()])
      .refine(
        (val) => {
          if (typeof val === "string") {
            const date = new Date(val);
            return !isNaN(date.getTime());
          }
          return true;
        },
        {
          message: "enrollment date is not valid",
        },
      )
      .optional(),
    completion_date: z
      .union([z.date(), z.string()])
      .refine(
        (val) => {
          if (typeof val === "string") {
            const date = new Date(val);
            return !isNaN(date.getTime());
          }
          return true;
        },
        {
          message: "completion date is not valid",
        },
      )
      .optional(),
    certificate_template: AsyncSchema.refine(
      (val) => val !== null && val.length > 0,
      {
        message: "certificate template is required",
      },
    ),
    remarks: z.string().optional(),
    full_name: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    phone: z.string().optional(),
    email: emailSchema.optional(),
    phone_number: z.string().optional(),
  })
  .refine(
    (data) => {
      const enrollment = new Date(data.enrollment_date ?? "");
      const completion = new Date(data.completion_date ?? "");

      if (isNaN(enrollment.getTime()) || isNaN(completion.getTime())) {
        return false;
      }

      enrollment.setHours(0, 0, 0, 0);
      completion.setHours(0, 0, 0, 0);

      return completion >= enrollment;
    },
    {
      message: "Completion date must not be before enrollment date",
      path: ["completion_date"],
    },
  );
const PolicySchema = z.object({
  description: z
    .string({ message: "Description is required" })
    .min(10, { message: "must be 10 character long." }),
});
const BlogSchema = z.object({
  title: z.string({ required_error: "Blog Title is required" }).min(2, {
    message: "Blog Title is required",
  }),
  slug: slugSchema,
  description: z
    .string({ required_error: "Blog Description is required" })
    .refine((val) => val.length >= 2, {
      message: "Blog Description is required",
    }),
  author_type: AsyncSchema.optional(),
  mentor: AsyncSchema.optional(),
  team_member: AsyncSchema.optional(),
  meta_title: z
    .string({ required_error: "Meta Title is required" })
    .min(2, { message: "Meta Title is required" }),
  meta_description: z
    .string({ required_error: "Meta Description is required" })
    .min(2, { message: "Meta Description is required" }),
  meta_keywords: z
    .string({ required_error: "Meta Keywords is required" })
    .optional()
    .nullable(),
  meta_author: z
    .string({ required_error: "Meta Author is required" })
    .min(2, { message: "Meta Author is required" }),
  header_code: z
    .string({ required_error: "Header Code is required" })
    .optional(),
  embedded_code: z
    .string({ required_error: "Embedded Code is required" })
    .optional(),
  excerpt: z
    .string({ required_error: "Excerpt is required" })
    .min(2, { message: "Excerpt is required" }),
  tags: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "tags is required",
  }),
  category: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "category is required",
  }),
  publish_date: z.any().optional().nullable(),
  featured_image: imageSchema,
  blog_type: z.any().optional(),
  json_ld: z.any().optional(),
});
const ProfileSchema = z
  .object({
    first_name: z.string({ message: "First Name is required" }).min(2, {
      message: "First Name is required",
    }),
    professional_image: imageSchema.optional().nullable(),
    last_name: z.string({ message: "Last Name is required" }).min(2, {
      message: "Last Name is required",
    }),
    email: z.string({ message: "Email is required" }).email().min(2, {
      message: "Email is required",
    }),
    current_password: z
      .string({
        required_error: "Current Password is required",
      })
      .min(6, {
        message: "Password must be at least 6 characters long",
      })
      .optional(),
    new_password: z
      .string({ required_error: "New Password is required" })
      .min(6, {
        message: "Password must be at least 6 characters long",
      })
      .optional(),
    confirm_password: z
      .string({
        required_error: "Confirm Password is required",
      })
      .min(6, {
        message: "Password must be at least 6 characters long",
      })
      .optional(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ["confirm_password"], // Target this field for the error
  });

const invoiceMoneySchema = z
  .union([z.string(), z.number()])
  .optional()
  .nullable()
  .refine(
    (val) =>
      val === undefined ||
      val === null ||
      val === "" ||
      (!Number.isNaN(Number(val)) && Number(val) >= 0),
    { message: "Enter a valid amount (0 or more)" },
  );

const StudentInvoiceSchema = z
  .object({
    full_name: z
      .string({ required_error: "Full Name is required" })
      .optional()
      .nullable(),
    email: z
      .string({ required_error: "Email is required" })
      .email()
      .optional()
      .nullable(),
    professional_image: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    is_masterclass: z.boolean().optional(),
    is_jrp: z.boolean().optional(),
    // Editable billing figures. The invoice keeps its own snapshot of the fee, so
    // correcting one here does not touch the course price.
    total_amount: invoiceMoneySchema,
    discount: invoiceMoneySchema,
    paid_amount: invoiceMoneySchema,
  })
  .superRefine((data, ctx) => {
    const total = Number(data.total_amount ?? 0);
    const discount = Number(data.discount ?? 0);
    const paid = Number(data.paid_amount ?? 0);
    if (
      !Number.isNaN(total) &&
      !Number.isNaN(discount) &&
      !Number.isNaN(paid) &&
      discount + paid > total
    ) {
      ctx.addIssue({
        path: ["total_amount"],
        code: z.ZodIssueCode.custom,
        message: "Discount plus paid amount cannot exceed the total amount",
      });
    }
  });

const StudentPlacementSchema = z.object({
  current_placement_company: z.string(),
  current_role: z.string(),
  student: AsyncSchema.refine((val) => val !== null && val.length > 0, {
    message: "category is required",
  }),
});
const BlogCategories = z.object({
  name: z.string({ message: "Name is Required" }).min(2, {
    message: "Name is required",
  }),
  is_show: z.boolean().default(false).optional(),
  media: imageSchema.optional(),
  id: z.any().optional(),
});
type BlogCategoryFormData = z.infer<typeof BlogCategories>;

const enterpriseTestimonialSchema = z.object({
  reviewer_name: z.string({ required_error: "Reviewer Name is required" }),
  reviewer_position_course: z.string({ required_error: "Position / Course is required" }),
  review_text: z.string().optional(),
  embed_link: z.string().optional(),
  rating_star: z.union([z.number(), z.string()]).optional().nullable(),
  profile_icon: z.any().optional().nullable(),
});
type EnterpriseTestimonialFormTypes = z.infer<typeof enterpriseTestimonialSchema>;

const enterpriseCherishMomentSchema = z.object({
  title: z.string({ required_error: "Title is required" }),
  sub_text: z.string({ required_error: "Sub Text is required" }),
  image_link: z.any().optional().nullable(),
});
type EnterpriseCherishMomentFormTypes = z.infer<typeof enterpriseCherishMomentSchema>;

const homeCherishMomentSchema = z.object({
  title: z.string({ required_error: "Title is required" }),
  sub_text: z.string({ required_error: "Sub Text is required" }),
  image_link: z.any().optional().nullable(),
});
type HomeCherishMomentFormTypes = z.infer<typeof homeCherishMomentSchema>;
type UserFormData = z.infer<typeof UserLoginSchema>;
type UserForgetFormData = z.infer<typeof UserForgetSchema>;
type UserVerifyOtpFormData = z.infer<typeof UserVerifyOtpSchema>;
type UserResetPasswordFormData = z.infer<typeof UserResetPasswordSchema>;
type SuccessStoriesFormData = z.infer<typeof SuccessStoriesSchema>;
// dashboard Types
type JourneyFormTypes = z.infer<typeof JourneyFormSchema>;
type CareerFormTypes = z.infer<typeof CareerFormSchema>;
type TestimonialDescriptionFormTypes = z.infer<
  typeof testimonialDescripionSchema
>;
type certificateFormData = z.infer<typeof certificateSchema>;
type IndustryAlliesFormData = z.infer<typeof IndustryAlliesSchema>;
type MomentsFormData = z.infer<typeof MomentsSchema>;
type TestimonialVideoFormTypes = z.infer<typeof testimonialVideoSchema>;
type TestimonialFormTypes = z.infer<
  typeof testimonialDescripionSchema & typeof testimonialVideoSchema
>;
type TeamMembersFormData = z.infer<typeof TeamMemberSchema>;
type TemplateFormData = z.infer<typeof TemplateFormSchmea>;
type TemplateFormDataGiveAway = z.infer<typeof TemplateFormSchmeaGiveaway>;
type FormsFormFormData = z.infer<typeof FormsFormSchema>;
type StudentFormData = z.infer<typeof StudentFormSchema>;
type CourseFormData = z.infer<typeof courseSchema>;
type TeamsLicenseData = z.infer<typeof TeamsLicenseSchema>;

type CareerFormData = z.infer<typeof CareerSchema>;

type CategoryFormData = z.infer<typeof CategorySchema>;
type DurationFormData = z.infer<typeof DurationSchema>;
type CourseBatchFormData = z.infer<typeof CourseBatchSchema>;
type BatchFormData = z.infer<typeof BatchSchema>;
type ContactUsMasterClassData = z.infer<typeof ContactUsMasterClassSchema>;
type PlacementFormData = z.infer<typeof placementSchema>;
type PayementFormData = z.infer<typeof PaymentSchema>;
type PaymentModeFormData = z.infer<typeof PaymentModeSchema>;
type PaymentDiscountFormData = z.infer<typeof PaymentDiscountSchema>;
type CustomPageFormData = z.infer<typeof CustomPageSchema>;
type MentorFormData = z.infer<typeof MentorSchema>;
type faqFormData = z.infer<typeof faqSchema>;
type PolicyFormData = z.infer<typeof PolicySchema>;
type enrollmentFormData = z.infer<typeof enrollmentSchema>;
type ContactUsFormData = z.infer<typeof ContactUsSchema>;
type BlogFormData = z.infer<typeof BlogSchema>;
type ProfileFormData = z.infer<typeof ProfileSchema>;
type BrandingFormData = z.infer<typeof BrandingFormSchema>;
type StudentPlacementFormData = z.infer<typeof StudentPlacementSchema>;
type StudentInvoiceFormData = z.infer<typeof StudentInvoiceSchema>;
type singleStudentEnrollmentFormData = z.infer<
  typeof singleStudentEnrollmentSchema
>;
type PopupFormData = z.infer<typeof PopupSchema>;
type BucketFormData = z.infer<typeof BucketSchema>;
export {
  BlogSchema,
  singleStudentEnrollmentSchema,
  CareerFormSchema,
  certificateSchema,
  courseSchema,
  PopupSchema,
  BrandingFormSchema,
  CustomPageSchema,
  enrollmentSchema,
  faqSchema,
  FormsFormSchema,
  IndustryAlliesSchema,
  JourneyFormSchema,
  MentorSchema,
  MomentsSchema,
  BlogCategories,
  PaymentModeSchema,
  PaymentSchema,
  placementSchema,
  PolicySchema,
  ProfileSchema,
  StudentFormSchema,
  SuccessStoriesSchema,
  TeamMemberSchema,
  TemplateFormSchmea,
  testimonialDescripionSchema,
  testimonialVideoSchema,
  TeamsLicenseSchema,
  UserForgetSchema,
  UserLoginSchema,
  UserResetPasswordSchema,
  UserVerifyOtpSchema,
  CategorySchema,
  DurationSchema,
  CareerSchema,
  ContactUsSchema,
  ContactUsMasterClassSchema,
  BucketSchema,
  CourseBatchSchema,
  BatchSchema,
  StudentPlacementSchema,
  PaymentDiscountSchema,
  StudentInvoiceSchema,
  TemplateFormSchmeaGiveaway,
  enterpriseTestimonialSchema,
  enterpriseCherishMomentSchema,
  homeCherishMomentSchema,
};
export type {
  BlogCategoryFormData,
  BlogFormData,
  CareerFormTypes,
  certificateFormData,
  CourseFormData,
  ContactUsFormData,
  ContactUsMasterClassData,
  CustomPageFormData,
  singleStudentEnrollmentFormData,
  enrollmentFormData,
  PopupFormData,
  faqFormData,
  FormsFormFormData,
  IndustryAlliesFormData,
  BrandingFormData,
  JourneyFormTypes,
  MentorFormData,
  MomentsFormData,
  PayementFormData,
  PaymentModeFormData,
  PlacementFormData,
  PolicyFormData,
  ProfileFormData,
  StudentFormData,
  SuccessStoriesFormData,
  TeamMembersFormData,
  TemplateFormData,
  TestimonialDescriptionFormTypes,
  TestimonialFormTypes,
  TestimonialVideoFormTypes,
  TeamsLicenseData,
  UserForgetFormData,
  UserFormData,
  UserResetPasswordFormData,
  UserVerifyOtpFormData,
  CategoryFormData,
  DurationFormData,
  CareerFormData,
  CourseBatchFormData,
  BatchFormData,
  StudentPlacementFormData,
  PaymentDiscountFormData,
  StudentInvoiceFormData,
  BucketFormData,
  TemplateFormDataGiveAway,
  EnterpriseTestimonialFormTypes,
  EnterpriseCherishMomentFormTypes,
  HomeCherishMomentFormTypes,
};
